import type { ThreadAttachment } from '@speles7172/message-console';
import type { ThreadReportRequest } from '@peles/shared';
import { apiClient } from './client';

/**
 * What a conversation can carry, beyond the words in it
 * (COMMUNICATIONS-DESIGN H4).
 *
 * ⚠️ **Separate from `api/notifications.ts` on purpose.** That module is the
 * *transport* — the object `@speles7172/message-console` is handed, whose shape
 * the package defines and whose identity must be stable. These are this
 * application's own endpoints, which the package knows nothing about: reports,
 * upload requests, and the question of which folder a conversation's files live
 * in. Putting them on the transport would be claiming the package asked for
 * them.
 */

/** Where a conversation's files live, as the server resolves it. */
export interface ThreadContext {
  /**
   * The library node the picker opens on and uploads land in.
   *
   * `null` when the conversation is about nothing the file tree knows — the
   * picker then opens at the root and there is nowhere to upload to.
   */
  nodeId: string | null;
  /** The event behind the record, when there is one. Null hides the reports. */
  eventId: string | null;
  entityType: string | null;
  entityId: string | null;
}

export async function fetchThreadContext(threadId: string): Promise<ThreadContext> {
  const { data } = await apiClient.get<ThreadContext>(`/threads/${threadId}/context`);
  return data;
}

/**
 * The same answer, for a record whose conversation does not exist yet.
 *
 * ⚠️ **Asked of the server, not derived here.** An expense's files are the
 * *event's* files, and the walk from one to the other is a fact about the
 * schema — a browser working it out would be a second copy of that rule, free
 * to disagree with the thread-keyed form about which folder a file went into.
 *
 * A record of `null` is a conversation about nothing: no folder, no event, and
 * no request worth making.
 */
export async function fetchRecordContext(
  record: { entityType: string; entityId: string } | null,
): Promise<ThreadContext> {
  if (record === null) return { nodeId: null, eventId: null, entityType: null, entityId: null };
  const { data } = await apiClient.get<ThreadContext>('/threads/record-context', {
    params: { entityType: record.entityType, entityId: record.entityId },
  });
  return data;
}

/**
 * A folder a conversation's incoming file may be filed into.
 *
 * ⚠️ **A library node id, not a `DocumentFolder` uuid.** The "ask for a file
 * back" dialog used to list legacy `document_folders` rows scoped to the
 * record's type — a tree events have essentially no rows in, so it answered
 * "No options" every time it was opened, and the server then validated against
 * that same wrong tree. A conversation's files are `file-client` files; these
 * are the folders under its record.
 */
export interface ThreadFolderOption {
  nodeId: string;
  /** `"Smith Wedding / Contracts"` — a name alone repeats across branches. */
  path: string;
}

/**
 * Where this conversation may file a file it asks for.
 *
 * ⚠️ **Asked of the server, and it is the permission as well as the list.** The
 * ask endpoint validates a submitted folder against this same walk, so the set
 * offered and the set accepted are one computation and cannot drift into a
 * dropdown whose entries the API refuses.
 */
export async function fetchThreadFolders(threadId: string): Promise<ThreadFolderOption[]> {
  const { data } = await apiClient.get<ThreadFolderOption[]>(`/threads/${threadId}/folders`);
  return data;
}

/** The same list, for a conversation that does not exist yet. */
export async function fetchRecordFolders(
  record: { entityType: string; entityId: string } | null,
): Promise<ThreadFolderOption[]> {
  if (record === null) return [];
  const { data } = await apiClient.get<ThreadFolderOption[]>('/threads/record-folders', {
    params: { entityType: record.entityType, entityId: record.entityId },
  });
  return data;
}

export async function fetchThreadAttachments(threadId: string): Promise<ThreadAttachment[]> {
  const { data } = await apiClient.get<ThreadAttachment[]>(`/threads/${threadId}/attachments`);
  return data;
}

/**
 * Attach files the library already holds.
 *
 * ⚠️ **Ids, never object keys** — the rule C5a settled. The server resolves
 * each through the file system's own authorizer, so a file the sender could not
 * see in the explorer cannot be attached to a conversation either.
 */
export async function attachLibraryFiles(
  threadId: string,
  fileIds: string[],
): Promise<ThreadAttachment[]> {
  const { data } = await apiClient.post<ThreadAttachment[]>(`/threads/${threadId}/attachments`, {
    fileIds,
  });
  return data;
}

/**
 * Upload one file from the device into the conversation.
 *
 * Three steps, and the first of them is why the browser never names a folder:
 * the server resolves the destination from the *thread*, so a file dropped into
 * a conversation about an event is filed under that event and nowhere else.
 */
export async function uploadFileToThread(
  threadId: string,
  file: File,
): Promise<ThreadAttachment | null> {
  const contentType = file.type || 'application/octet-stream';
  const { data: ticket } = await apiClient.post<{ uploadUrl: string; fileId: string }>(
    `/threads/${threadId}/attachments/upload-url`,
    { filename: file.name, contentType },
  );

  const response = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  // The status, because "Upload failed" is the same sentence for a link that
  // expired (403) and a bucket that is not there (404), and the two are fixed
  // by different people.
  if (!response.ok) {
    throw new Error(`“${file.name}” could not be uploaded to file storage (${response.status}).`);
  }

  // Until this lands the file is not listed anywhere — so the confirm is not
  // optional bookkeeping, it is what makes the upload exist.
  const { data } = await apiClient.post<ThreadAttachment | null>(
    `/threads/${threadId}/attachments/complete`,
    { fileId: ticket.fileId, byteSize: file.size },
  );
  return data;
}

export async function removeThreadAttachment(
  threadId: string,
  attachmentId: string,
): Promise<void> {
  await apiClient.delete(`/threads/${threadId}/attachments/${attachmentId}`);
}

export async function fetchAttachmentUrl(
  threadId: string,
  attachmentId: string,
): Promise<string | null> {
  const { data } = await apiClient.get<{ downloadUrl: string }>(
    `/threads/${threadId}/attachments/${attachmentId}/download-url`,
  );
  return data?.downloadUrl ?? null;
}

/** Render the chosen reports as PDFs and attach them. */
export async function attachThreadReports(
  threadId: string,
  reports: ThreadReportRequest[],
): Promise<ThreadAttachment[]> {
  const { data } = await apiClient.post<ThreadAttachment[]>(
    `/threads/${threadId}/attachments/reports`,
    { reports },
  );
  return data;
}

export interface ThreadUploadLinkInput {
  folderId?: string | null;
  fileTypes?: string[];
  label?: string | null;
  note?: string | null;
}

/**
 * Ask this conversation's people for a file back.
 *
 * Answers how many links were made and how many emails went out — the two are
 * not the same number when a mail provider is having a bad day, and the caller
 * should be able to say so.
 */
export async function requestThreadUpload(
  threadId: string,
  input: ThreadUploadLinkInput,
): Promise<{ requested: number; emailed: number }> {
  const { data } = await apiClient.post<{ requested: number; emailed: number }>(
    `/threads/${threadId}/upload-link`,
    input,
  );
  return data;
}
